import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { RegistrationFlow, UiNodeInputAttributes, UpdateRegistrationFlowBody } from '@ory/client';
import { AxiosError } from 'axios';
import { getOrySdk } from '../../services/ory';
import { handleFlowError } from '../../services/oryErrors';
import { useAuth } from '../../context';
import { SocialLoginButtons } from '../../components/auth/SocialLoginButtons';
import { client } from '../../services/api/familypal/config';

const REFERRAL_STORAGE_KEY = 'familypal_referral_code';

export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { syncSession, isAuthenticated } = useAuth();

  const [flow, setFlow] = useState<RegistrationFlow | undefined>();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get flow params from URL
  const flowId = searchParams.get('flow');
  const returnTo = searchParams.get('return_to');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  // Initialize or fetch registration flow
  useEffect(() => {
    if (flow) {
      setIsLoading(false);
      return;
    }

    const sdk = getOrySdk();

    // If ?flow=... was in the URL, fetch it
    if (flowId) {
      sdk
        .getRegistrationFlow({ id: flowId })
        .then(({ data }) => {
          setFlow(data);
          extractDefaults(data);
        })
        .catch(handleFlowError(navigate, 'registration', setFlow, setError))
        .catch((err) => {
          setError(err.message || 'Failed to load registration flow');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Otherwise create a new flow
    sdk
      .createBrowserRegistrationFlow({
        returnTo: returnTo ? String(returnTo) : undefined,
      })
      .then(({ data }) => {
        setFlow(data);
        extractDefaults(data);
        // Update URL with flow ID
        window.history.replaceState(null, '', `/signup?flow=${data.id}`);
      })
      .catch(handleFlowError(navigate, 'registration', setFlow, setError))
      .catch((err) => {
        setError(err.message || 'Failed to initialize registration');
      })
      .finally(() => setIsLoading(false));
  }, [flowId, flow, navigate, returnTo]);

  // Extract default values from flow
  const extractDefaults = (flowData: RegistrationFlow) => {
    flowData.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        if (attrs.name === 'traits.email' && attrs.value) {
          setEmail(String(attrs.value));
        }
        if (attrs.name === 'traits.name.first' && attrs.value) {
          setFirstName(String(attrs.value));
        }
        if (attrs.name === 'traits.name.last' && attrs.value) {
          setLastName(String(attrs.value));
        }
      }
    });
  };

  // Get CSRF token from flow
  const getCsrfToken = useCallback((): string => {
    let csrf = '';
    flow?.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        if (attrs.name === 'csrf_token' && attrs.value) {
          csrf = String(attrs.value);
        }
      }
    });
    return csrf;
  }, [flow]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;

    setIsSubmitting(true);
    setError(null);

    const sdk = getOrySdk();

    const body: UpdateRegistrationFlowBody = {
      method: 'password',
      password: password,
      traits: {
        email: email,
        name: {
          first: firstName,
          last: lastName,
        },
      },
      csrf_token: getCsrfToken(),
    };

    try {
      const response = await sdk.updateRegistrationFlow({
        flow: flow.id,
        updateRegistrationFlowBody: body,
      });

      // Check for continue_with actions (e.g., verification)
      const continueWith = response.data.continue_with;
      if (continueWith && continueWith.length > 0) {
        for (const action of continueWith) {
          if (action.action === 'show_verification_ui') {
            navigate('/verification?flow=' + (action as any).flow?.id, { replace: true });
            return;
          }
        }
      }

      // Registration successful - sync session and redirect
      if (response.data.session) {
        await syncSession();

        // Track referral if one was stored
        const referralCode = localStorage.getItem(REFERRAL_STORAGE_KEY);
        if (referralCode) {
          try {
            await client.post({
              url: '/familypal/v1/identity/referral/track',
              body: { referral_code: referralCode, source: 'web' },
            });
          } catch {
            // Non-critical — don't block signup
          }
          localStorage.removeItem(REFERRAL_STORAGE_KEY);
        }

        if (flow.return_to) {
          window.location.href = flow.return_to;
        } else {
          navigate('/', { replace: true });
        }
      } else {
        // No session returned - might need email verification
        navigate('/login', {
          replace: true,
          state: { message: 'Account created! Please check your email to verify your account.' },
        });
      }
    } catch (err) {
      const axiosErr = err as AxiosError<any>;

      try {
        await handleFlowError(navigate, 'registration', setFlow, setError)(axiosErr);
      } catch (unhandledErr) {
        if (axiosErr.response?.status === 400) {
          setFlow(axiosErr.response.data);
          extractErrorMessages(axiosErr.response.data);
        } else {
          setError(axiosErr.message || 'Registration failed. Please try again.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Extract error messages from flow UI
  const extractErrorMessages = (flowData: RegistrationFlow) => {
    const messages: string[] = [];

    flowData.ui.messages?.forEach((m) => {
      if (m.type === 'error') {
        messages.push(m.text);
      }
    });

    flowData.ui.nodes?.forEach((node) => {
      node.messages?.forEach((m) => {
        if (m.type === 'error') {
          messages.push(m.text);
        }
      });
    });

    if (messages.length > 0) {
      setError(messages.join(' '));
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight={700} color="primary">
              FamilyPal
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Create your account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                margin="normal"
                required
                autoComplete="given-name"
                autoFocus
              />
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                margin="normal"
                required
                autoComplete="family-name"
              />
            </Box>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText="Must be at least 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || !email || !password || !firstName || !lastName}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
          </form>

          <SocialLoginButtons flow={flow} disabled={isSubmitting} />

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login">
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupPage;
